// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO, } from 'wdna-components';
import { GridApi, RowDataChangedEvent, GridReadyEvent, RowNode } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageBoxWarning, messageBoxInfo, messageInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import AlarmsEdit from '../forms/alarms/Edit';

import { alarm, AlarmListDO, MsgRS } from '../../requests';
import { company, CompanyDO } from '../../../management/requests';
import { sensor, SensorListDO } from '../../../sensors/requests';

type CustonAlarmListDO = AlarmListDO & {
  companyName: string;
};

type Props = unknown;

interface State {
  loading: boolean;
  showGrid?: boolean;
  update?: string;
  marked: number;

  data?: CustonAlarmListDO[];
}

export default class Alarms extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;
  private gridApi: GridApi | undefined;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      duplicate: true,
      add: true,
      toggleItem: true,
      del: true,
    };

    this.state = {
      loading: false,
      showGrid: false,
      update: window.getStr(),
      marked: 0,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const [companyArray, sensorArray, alarmsTable] = await Promise.all([
        company<CompanyDO[]>('list'),
        sensor<SensorListDO[]>('list'),
        alarm<CustonAlarmListDO[]>('list'),
      ]);
      if (this.mounted) {
        this.setState({
          data: this.addExtraColumns(alarmsTable || [], companyArray || [], sensorArray || []),
          marked: 0,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public addExtraColumns = (data: CustonAlarmListDO[], companyArray: CompanyDO[], sensorArray: SensorListDO[]): any[] => {
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = user.type === 'adm';

    const filteredData: CustonAlarmListDO[] = data.filter((item: CustonAlarmListDO) => {
      const selectedSensor: SensorListDO | undefined = sensorArray.find((sens: SensorListDO) => sens.id === +item.sensorId);
      const selectedCompany: CompanyDO | undefined = companyArray.find((comp: CompanyDO) => comp.id === +(selectedSensor?.companyId || 0));
      item.companyName = selectedCompany?.name || ''; // eslint-disable-line no-param-reassign

      return userIsAdmin || (user.extra && user.extra.company && item.companyName === user.extra.company);
    });

    return filteredData;
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onDelete = (value: CustonAlarmListDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      alarm('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public onEdit = (value?: CustonAlarmListDO): void => {
    window.store.dispatch(removeTab('AE'));
    const rowId: number | undefined = value?.id;
    const duplicate: boolean = false;
    setTimeout(() => {
      this.loadTab(duplicate, rowId);
    }, 0);
  };

  public onDuplicate = (value: CustonAlarmListDO): void => {
    window.store.dispatch(removeTab('AE'));
    const rowId: number = value.id;
    const duplicate: boolean = true;
    setTimeout(() => {
      this.loadTab(duplicate, rowId);
    }, 0);
  };

  public loadTab = (duplicate: boolean, rowId?: number): void => {
    const tabId: string = 'AE';
    const title: string = rowId ? `Alarm Edit: ${rowId}` : 'Alarm New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: AlarmsEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'ALARMS',
        title: 'Alarms',
        notify: this.notify,
        duplicate,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public countSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setState({
      update: window.getStr(),
      marked,
    });
  };

  public activateSelected = (): void => {
    messageBoxInfo('Activate', 'Are you sure you want to activate the selected alarms?', () => {
      this.bulkActivation(true);
    });
  };

  public deactivateSelected = (): void => {
    messageBoxInfo('Deactivate', 'Are you sure you want to deactivate the selected alarms?', () => {
      this.bulkActivation(false);
    });
  };

  public bulkActivation = (active: boolean): void => {
    if (this.gridApi) {
      const selectedNodes: RowNode[] = this.gridApi.getSelectedNodes();
      const data: number[] = selectedNodes.map((row: RowNode): number => { return (row.data as CustonAlarmListDO).id; });

      this.loadingStart();
      alarm<MsgRS>('status', undefined, data, { params: { active } })
        ?.then((resp: MsgRS) => {
          messageInfo('Success', resp.message, 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public render(): JSX.Element {
    const { marked, showGrid } = this.state;

    const columnsToHide: string[] = [
      'mailSubject',
      'mailBody',
      'typeSensorVariableId',
      'typeSensorVariableFormula',
      'actionId',
      'gatewayId',
      'smsBody',
      'voiceBody',
      'sensorId',
      'repeat',
      'msgUnique',
      'msgUniqueSent',
    ];
    const columnAlias: Record<string, string> = {
      selectedRow: ' ',
      id: 'ID',
      active: 'Active',
      sensorCode: 'Sensor Code',
      sensorName: 'Sensor Name',
      companyName: 'Company',
      actionName: 'Action',
      mailTo: 'Email',
      launch: 'Launched',
      disconnection: 'Disconnected',
      min: 'Min',
      max: 'Max',
      smsTo: 'Phone SMS',
      voiceTo: 'Phone Voice',
      minutes: 'Outside Bounds',
      accumulate: 'Cumulative',
      dateUpdate: 'Last Update',
    };
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      mailTo: { textAlign: 'left' }, // to
      launch: { textAlign: 'right' }, // ta
      disconnection: { textAlign: 'right' }, // nc
      min: { textAlign: 'right' },
      max: { textAlign: 'right' },
      smsTo: { textAlign: 'center' },
      voiceTo: { textAlign: 'center' },
      minutes: { textAlign: 'right' },
      accumulate: { textAlign: 'center' }, // tac_alarm
      active: { textAlign: 'center' },
      sensorCode: { textAlign: 'right' },
      sensorName: { textAlign: 'left' },
      actionName: { textAlign: 'left' },
      companyName: { textAlign: 'left' },
      dateUpdate: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };
    const selectRowProps: Record<string, string> = {
      mode: 'checkbox',
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
    };

    return (
      <div id="content" className="content">
        <div className="row">
          <div className="col-6 wdna-padding">
            <span>
              <b>{`Total Rows Selected: ${marked}`}</b>
            </span>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-success" onClick={this.activateSelected}>
              <b>Activate selected</b>
            </button>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-danger" onClick={this.deactivateSelected}>
              <b>Deactivate selected</b>
            </button>
          </div>
        </div>
        <div style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={this.state.data || []}
            alias={columnAlias}
            hiddenCols={columnsToHide}
            loading={this.state.loading}
            buttons={this.buttons}
            rowDataChanged={this.rowDataChanged}
            customCellStyles={columnStyles}
            customColumnsDefs={customColumnsDefs}
            onEdit={this.onEdit}
            onDelete={this.onDelete}
            onAdd={this.onEdit}
            onDuplicate={this.onDuplicate}
            // TODO: review ??
            // selectRow={selectRowProps}
            onGridReady={this.onGridReady}
            onSelectionChanged={this.countSelected}
            selection="multiple"
            pagination={false}
            showRowNumber
          />
        </div>
      </div>
    );
  }
}
